<template>
  <transition name="fs-fade">
    <div class="pim-fullscreen">
      <!-- Header -->
      <div class="pim-header">
        <div class="pim-header__left">
          <h5 class="pim-header__title">{{ product.name }}</h5>
          <span class="pim-header__subtitle">
            {{ product.code }} · {{ rows.length }} factura(s) con este producto
          </span>
        </div>
        <div class="pim-header__actions">
          <el-tooltip content="Descargar Excel" placement="bottom">
            <button
              v-if="rows.length"
              class="pim-action-btn"
              @click="downloadExcel"
            >
              <i class="el-icon-download"></i>
            </button>
          </el-tooltip>
          <button class="pim-close" @click="$emit('close')">
            <i class="el-icon-close"></i>
          </button>
        </div>
      </div>

      <!-- Body scrollable -->
      <div class="pim-body" v-loading="loading">
        <!-- KPIs -->
        <div v-if="!loading && rows.length" class="pim-kpis">
          <div class="pim-kpi">
            <span class="pim-kpi__label">Facturas</span>
            <span class="pim-kpi__value">{{ rows.length }}</span>
          </div>
          <div class="pim-kpi">
            <span class="pim-kpi__label">Unidades vendidas</span>
            <span class="pim-kpi__value">{{ totalQty }}</span>
          </div>
          <div class="pim-kpi">
            <span class="pim-kpi__label">Subtotal facturado</span>
            <span class="pim-kpi__value">${{ formatNum(totalSubtotal) }}</span>
          </div>
          <div class="pim-kpi pim-kpi--accent">
            <span class="pim-kpi__label">Clientes distintos</span>
            <span class="pim-kpi__value">{{ uniqueClients }}</span>
          </div>
        </div>

        <!-- Empty -->
        <div v-if="!loading && rows.length === 0" class="pim-empty">
          <i class="el-icon-document"></i>
          <p>No hay facturas para este producto en el período seleccionado</p>
        </div>

        <!-- Tabla -->
        <template v-if="!loading && rows.length">
          <div class="table-items-header mt-4">
            <b-row>
              <b-col md="2"><h6 class="h6 ml-2">Factura</h6></b-col>
              <b-col md="3"><h6 class="h6">Cliente</h6></b-col>
              <b-col md="2"><h6 class="h6">Fecha</h6></b-col>
              <b-col v-if="product.alternative_code" md="1"
                ><h6 class="h6">Código</h6></b-col
              >
              <b-col :md="product.alternative_code ? 1 : 2"
                ><h6 class="h6">Cant.</h6></b-col
              >
              <b-col md="1"><h6 class="h6">P. Unit.</h6></b-col>
              <b-col md="1"><h6 class="h6">Subtotal</h6></b-col>
              <b-col md="1"><h6 class="h6">Total</h6></b-col>
            </b-row>
          </div>

          <div
            v-for="row in rows"
            :key="row.invoice_id"
            class="card table-items-body"
          >
            <div class="card-body table-items-content">
              <b-row class="align-items-center">
                <b-col md="2" class="table-col-item">
                  <span class="item-col-code font-semi-bold">{{
                    row.number_invoice
                  }}</span>
                </b-col>
                <b-col md="3" class="table-col-item">
                  <div>
                    <span class="font-bold text-primary str-limit-space">{{
                      row.client_name
                    }}</span>
                    <br />
                    <small class="text-muted">{{ row.client_doc }}</small>
                  </div>
                </b-col>
                <b-col md="2" class="table-col-item">
                  <span class="font-semi-bold">{{ row.emission_date }}</span>
                </b-col>
                <b-col
                  v-if="product.alternative_code"
                  md="1"
                  class="table-col-item"
                >
                  <span
                    class="pim-code-badge"
                    :class="
                      row.product_code === product.alternative_code
                        ? 'pim-code-badge--alt'
                        : 'pim-code-badge--normal'
                    "
                  >
                    {{
                      row.product_code === product.alternative_code
                        ? "Alt."
                        : "Normal"
                    }}
                  </span>
                </b-col>
                <b-col
                  :md="product.alternative_code ? 1 : 2"
                  class="table-col-item"
                >
                  <span class="font-semi-bold">{{ row.quantity }}</span>
                </b-col>
                <b-col md="1" class="table-col-item">
                  <span class="font-semi-bold"
                    >${{ formatNum(row.unit_price) }}</span
                  >
                </b-col>
                <b-col md="1" class="table-col-item">
                  <span class="font-semi-bold"
                    >${{ formatNum(row.subtotal) }}</span
                  >
                </b-col>
                <b-col md="1" class="table-col-item">
                  <span class="font-semi-bold"
                    >${{ formatNum(row.total) }}</span
                  >
                </b-col>
              </b-row>
            </div>
          </div>
        </template>
      </div>
    </div>
  </transition>
</template>

<script>
import * as XLSX from "xlsx";
import reportService from "../services/reportService";

export default {
  name: "ProductInvoicesModal",
  props: {
    product: { type: Object, required: true },
    searchDates: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      rows: [],
      loading: false
    };
  },
  computed: {
    totalQty() {
      return this.rows.reduce((s, r) => s + (r.quantity || 0), 0);
    },
    totalSubtotal() {
      return this.rows.reduce((s, r) => s + (r.subtotal || 0), 0);
    },
    uniqueClients() {
      return new Set(this.rows.map(r => r.client_doc).filter(Boolean)).size;
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await reportService.getProductInvoices(this.product.id, {
          params: { ...this.searchDates }
        });
        if (res.data.success) this.rows = res.data.data || [];
      } finally {
        this.loading = false;
      }
    },
    formatNum(val) {
      if (val === null || val === undefined) return "0.00";
      return Number(val).toLocaleString("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    downloadExcel() {
      const headers = [
        "Factura",
        "Cliente",
        "RUC / CI",
        "Fecha",
        "Cantidad",
        "P. Unitario",
        "Subtotal",
        "Total"
      ];
      const data = this.rows.map(r => [
        r.number_invoice,
        r.client_name,
        r.client_doc,
        r.emission_date,
        r.quantity,
        r.unit_price,
        r.subtotal,
        r.total
      ]);
      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Ventas");
      const filename = `ventas_${this.product.code ||
        this.product.name}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, filename);
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #2d2b55;
$accent: #3ecfb4;
$border: #eef0f8;

/* ── Overlay fullscreen ── */
.pim-fullscreen {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: #f7f8fb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Header ── */
.pim-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 60px;
  min-height: 60px;
  background: #fff;
  border-bottom: 1.5px solid $border;
  flex-shrink: 0;

  &__left {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $primary;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__subtitle {
    font-size: 12px;
    color: #aaa;
    margin-top: 1px;
  }
}

.pim-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.pim-action-btn {
  width: 36px;
  height: 36px;
  border: 1.5px solid $border;
  background: #fff;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: $primary;
  flex-shrink: 0;
  transition: all 0.15s;

  &:hover {
    border-color: $accent;
    color: $accent;
    background: rgba($accent, 0.05);
  }
}

.pim-close {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: $primary;
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover {
    background: #f0f2f7;
  }
}

/* ── Body ── */
.pim-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px 32px;

  @media (max-width: 768px) {
    padding: 16px;
  }
}

/* ── KPIs ── */
.pim-kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.pim-kpi {
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 12px;
  padding: 14px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &--accent {
    border-color: rgba($accent, 0.4);
    background: rgba($accent, 0.04);
  }

  &__label {
    font-size: 11px;
    color: #aaa;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
  }

  &__value {
    font-size: 22px;
    font-weight: 700;
    color: $primary;
  }
}

/* ── Empty ── */
.pim-empty {
  text-align: center;
  padding: 80px 20px;
  color: #aaa;

  i {
    font-size: 40px;
    color: #c0c4d0;
    display: block;
    margin-bottom: 12px;
  }

  p {
    font-size: 14px;
    margin: 0;
  }
}

/* ── Code badge ── */
.pim-code-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;

  &--normal {
    background: #eef0f8;
    color: #2d2b55;
  }

  &--alt {
    background: #e1f5ee;
    color: #1d9e75;
  }
}

/* ── Transition ── */
.fs-fade-enter-active,
.fs-fade-leave-active {
  transition: opacity 0.2s ease;
}
.fs-fade-enter,
.fs-fade-leave-to {
  opacity: 0;
}
</style>
